import React, { useState, useEffect, useContext } from 'react';
import { fmtDateTime as _fmtDateTime } from '@/lib/dates';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, Trash2, FileDown } from 'lucide-react';

const SubmissionRepository = () => {
  const { token } = useContext(AuthContext);
  const [submissions, setSubmissions] = useState([]);
  const [apps, setApps] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [filter, setFilter] = useState({ app_id: '', status: '' });

  useEffect(() => {
    fetchSubmissions();
    fetchApps();
  }, [filter]);

  const fetchSubmissions = async () => {
    try {
      const params = {};
      if (filter.app_id) params.app_id = filter.app_id;
      if (filter.status) params.status = filter.status;
      
      const response = await axios.get(`${API}/submissions`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setSubmissions(response.data);
    } catch (error) {
      toast.error('Failed to fetch submissions');
    }
  };

  const fetchApps = async () => {
    try {
      const response = await axios.get(`${API}/apps`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApps(response.data);
    } catch (error) {
      console.error('Failed to fetch apps');
    }
  };

  const handleView = async (submissionId) => {
    try {
      const response = await axios.get(`${API}/submissions/${submissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedSubmission(response.data);
      setIsViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load submission details');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    try {
      await axios.delete(`${API}/submissions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Submission deleted');
      fetchSubmissions();
    } catch (error) {
      toast.error('Failed to delete submission');
    }
  };

  const updateSubmissionStatus = async (id, newStatus) => {
    try {
      await axios.put(`${API}/submissions/${id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated');
      fetchSubmissions();
      if (selectedSubmission?.id === id) {
        setSelectedSubmission({ ...selectedSubmission, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getAppName = (appId) => {
    const app = apps.find(a => a.id === appId);
    return app ? app.name : 'Unknown App';
  };

  const formatDate = (dateString) => {
    return _fmtDateTime(dateString);
  };

  return (
    <div>
      <Card>
        <CardHeader>
          <CardTitle>Form Submission Repository</CardTitle>
          <div className="flex gap-3 mt-4">
            <div className="flex-1">
              <Label htmlFor="filter-app" className="text-xs">Filter by App</Label>
              <select
                id="filter-app"
                className="w-full border rounded-md px-3 py-2 mt-1"
                value={filter.app_id}
                onChange={(e) => setFilter({ ...filter, app_id: e.target.value })}
                data-testid="filter-app-select"
              >
                <option value="">All Apps</option>
                {apps.map(app => (
                  <option key={app.id} value={app.id}>{app.name}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <Label htmlFor="filter-status" className="text-xs">Filter by Status</Label>
              <select
                id="filter-status"
                className="w-full border rounded-md px-3 py-2 mt-1"
                value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                data-testid="filter-status-select"
              >
                <option value="">All Statuses</option>
                <option value="submitted">Submitted</option>
                <option value="reviewed">Reviewed</option>
                <option value="approved">Approved</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {submissions.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No submissions found.</p>
            ) : (
              submissions.map((submission) => (
                <div key={submission.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50" data-testid="submission-item">
                  <div className="flex-1">
                    <h3 className="font-medium">{getAppName(submission.app_id)}</h3>
                    <p className="text-sm text-slate-500">
                      Submitted: {formatDate(submission.submitted_at)} • 
                      <span className={`ml-1 font-medium ${
                        submission.status === 'submitted' ? 'text-blue-600' :
                        submission.status === 'reviewed' ? 'text-yellow-600' :
                        submission.status === 'approved' ? 'text-green-600' :
                        'text-slate-600'
                      }`}>
                        {submission.status}
                      </span>
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleView(submission.id)}
                      data-testid="view-submission-btn"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(submission.id)}
                      data-testid="delete-submission-btn"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* View Submission Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Submission Details</DialogTitle>
          </DialogHeader>
          {selectedSubmission && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">App</Label>
                  <p className="font-medium">{getAppName(selectedSubmission.app_id)}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Status</Label>
                  <p className="font-medium">{selectedSubmission.status}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Submitted At</Label>
                  <p className="font-medium">{formatDate(selectedSubmission.submitted_at)}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Submission ID</Label>
                  <p className="text-xs font-mono">{selectedSubmission.id}</p>
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-500">Form Data</Label>
                <pre className="mt-2 p-4 bg-slate-50 rounded-lg text-sm overflow-x-auto">
                  {JSON.stringify(selectedSubmission.data, null, 2)}
                </pre>
              </div>

              {selectedSubmission.photos && selectedSubmission.photos.length > 0 && (
                <div>
                  <Label className="text-xs text-slate-500">Photos</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {selectedSubmission.photos.map((photo, index) => (
                      <div key={index} className="aspect-square bg-slate-100 rounded-lg flex items-center justify-center">
                        <span className="text-xs text-slate-400">Photo {index + 1}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs text-slate-500 mb-2 block">Update Status</Label>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateSubmissionStatus(selectedSubmission.id, 'reviewed')}
                    data-testid="mark-reviewed-btn"
                  >
                    Mark Reviewed
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateSubmissionStatus(selectedSubmission.id, 'approved')}
                    data-testid="mark-approved-btn"
                  >
                    Mark Approved
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateSubmissionStatus(selectedSubmission.id, 'archived')}
                    data-testid="mark-archived-btn"
                  >
                    Archive
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubmissionRepository;